import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Loader2, Building2, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { BNTLogo } from "@/pages/layoutChrome";
import {
  ACCESS_CONTEXT_SELECTION_PATH,
  applyWorkModuleForAccessContext,
  CONTEXT_TYPE_AUXILIARY,
  CONTEXT_TYPE_OWNER,
  requiresAccessContextSelection,
  resolvePathAfterAccessContext,
} from "@/lib/workspaceAccessContext";
import { resolvePostLoginPath } from "@/lib/postLoginRedirect";

function contextIcon(contextType) {
  if (contextType === CONTEXT_TYPE_AUXILIARY) return Users;
  return Building2;
}

function contextSubtitle(ctx) {
  if (ctx.context_type === CONTEXT_TYPE_OWNER || ctx.type === CONTEXT_TYPE_OWNER) {
    const org = ctx.organization_name || "Mi negocio";
    return `${org} · Comprador titular`;
  }
  const org = ctx.organization_name || "Despacho";
  const role = ctx.role || "auxiliar";
  return `${org} · Rol: ${role}`;
}

function resolveContextKey(ctx) {
  return ctx.context_key || ctx.id;
}

export default function SeleccionarContextoAcceso() {
  const { user, setUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const switchMode = searchParams.get("cambiar") === "1";
  const [contexts, setContexts] = useState(() => user?.access_contexts || []);
  const [loadingList, setLoadingList] = useState(!user?.access_contexts?.length);
  const [busyKey, setBusyKey] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/contexts");
        if (cancelled) return;
        const items = Array.isArray(data.contexts) ? data.contexts : data.items;
        setContexts(Array.isArray(items) ? items : []);
      } catch {
        if (!cancelled && user?.access_contexts?.length) {
          setContexts(user.access_contexts);
        }
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.access_contexts]);

  useEffect(() => {
    if (!user) return;
    if (!switchMode && !requiresAccessContextSelection(user)) {
      navigate(resolvePostLoginPath(user), { replace: true });
      return;
    }
    if (!switchMode && contexts.length === 1) {
      navigate(resolvePostLoginPath(user), { replace: true });
    }
  }, [user, switchMode, contexts.length, navigate]);

  const onSelect = async (contextKey) => {
    setError("");
    setBusyKey(contextKey);
    try {
      const { data } = await api.post("/auth/select-access-context", { context_id: contextKey });
      setUser(data);
      applyWorkModuleForAccessContext(data);
      if (typeof refreshUser === "function") {
        await refreshUser();
      }
      navigate(resolvePathAfterAccessContext(data), { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No se pudo cambiar el contexto de acceso.");
    } finally {
      setBusyKey("");
    }
  };

  const activeKey = user?.active_context_id
    || user?.active_access_context?.active_context_id
    || user?.active_access_context?.context_key;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-lg space-y-6">
        <div className="flex justify-center">
          <BNTLogo size="md" showDefault productTagline={false} />
        </div>
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-4">
          <div>
            <h1 className="text-xl font-bold text-[#0F2D5C]" style={{ fontFamily: "'Chivo'" }}>
              Selecciona cómo deseas entrar
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              {switchMode
                ? "Puede cambiar de contexto sin cerrar sesión."
                : "Su cuenta tiene acceso a más de un espacio. Elija con cuál desea operar ahora."}
            </p>
            {user?.email ? (
              <p className="text-xs text-slate-500 font-mono mt-2">{user.email}</p>
            ) : null}
          </div>

          {loadingList ? (
            <div className="flex items-center justify-center py-8 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Cargando opciones…
            </div>
          ) : (
            <div className="space-y-2" data-testid="access-context-options">
              {contexts.map((ctx) => {
                const ctxKey = resolveContextKey(ctx);
                const ctxType = ctx.context_type || ctx.type;
                const Icon = contextIcon(ctxType);
                const isActive = activeKey === ctxKey;
                const isBusy = busyKey === ctxKey;
                return (
                  <button
                    key={ctxKey}
                    type="button"
                    data-testid={`access-context-${ctxType}`}
                    disabled={Boolean(busyKey)}
                    onClick={() => onSelect(ctxKey)}
                    className={`w-full text-left rounded-sm border px-4 py-3 transition-colors flex items-start gap-3 ${
                      isActive
                        ? "border-[#3DA873] bg-emerald-50/60"
                        : "border-slate-200 hover:border-[#0F2D5C]/40 hover:bg-slate-50"
                    }`}
                  >
                    <Icon className="w-5 h-5 text-[#0F2D5C] shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-[#0F2D5C]">{ctx.label}</div>
                      <div className="text-xs text-slate-500 truncate">{contextSubtitle(ctx)}</div>
                      {isActive && switchMode ? (
                        <div className="text-[10px] text-emerald-700 mt-1">Contexto activo</div>
                      ) : null}
                    </div>
                    {isBusy ? <Loader2 className="w-4 h-4 animate-spin text-slate-400 shrink-0" /> : null}
                  </button>
                );
              })}
            </div>
          )}

          {!switchMode ? (
            <p className="text-xs text-slate-500 leading-snug border-t border-slate-100 pt-3">
              Puedes cambiar de contexto más adelante desde tu perfil (menú lateral →{" "}
              <strong>Cambiar contexto</strong>).
            </p>
          ) : null}

          {error ? (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
              {error}
            </div>
          ) : null}

          {switchMode ? (
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-sm"
              onClick={() => navigate(resolvePathAfterAccessContext(user), { replace: true })}
              disabled={Boolean(busyKey)}
            >
              Cancelar y volver
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export { ACCESS_CONTEXT_SELECTION_PATH };
